import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { reportForcedSignOut } from '../utils/auth-telemetry';
import { firstValueFrom, combineLatest } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';
import { Auth } from '@angular/fire/auth';
import { environment } from 'src/environments/environment';

// Import Akita queries and facades
import { AuthQuery } from '../state/auth.query';
import { UserQuery } from '../state/user.query';
import { UserFacadeService } from '../state/user-facade.service';
import { AuthService, RETURN_URL_OWNER_KEY, toOwnerUid } from '../services/auth.service';

export const NO_TEFTER_ACCOUNT = 'NO_TEFTER_ACCOUNT';
export const NO_USER_ID = 'NO_USER';

interface SessionData {
  uid: string;
  timestamp: number;
  registered: boolean;
  orgId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private router = inject(Router);
  private auth = inject(Auth);
  private authQuery = inject(AuthQuery);
  private userQuery = inject(UserQuery);
  private userFacade = inject(UserFacadeService);
  private authService = inject(AuthService);

  // Track guard check attempts to prevent infinite loops
  private attemptCount = 0;
  private readonly MAX_ATTEMPTS = 3;

  async canActivate(_next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const currentRoute = state.url;
    this.attemptCount++;

    try {
      // If we've tried too many times, redirect to login
      if (this.attemptCount > this.MAX_ATTEMPTS) {
        this.attemptCount = 0;
        return this.navigateToSpecificPage(currentRoute, '/login');
      }

      // Wait for both auth and user to be initialized
      const authData = await firstValueFrom(
        combineLatest([
          this.userQuery.user$,
          this.authQuery.idToken$,
          this.authQuery.initialized$,
          this.userQuery.initialized$,
        ]).pipe(
          // Wait until both auth and user are initialized
          skipWhile(([user, token, _authInit, _userInit]) => {
            // Still waiting for initialization
            if (!_authInit || !_userInit) {
              return true;
            }

            // No token available
            if (!token) {
              const previousSession = this.getPreviousSessionData();
              if (previousSession) {
                if (this.attemptCount <= 2) {
                  this.reloadAuthData();
                }
                return true;
              }
            }

            // Token/user mismatch check
            if (token && user && user.id !== token.claims.sub) {
              localStorage.removeItem('userSessionData');
              return true;
            }

            // If user is undefined but we have previous session
            if (!user) {
              const previousSession = this.getPreviousSessionData();
              if (previousSession) {
                if (this.attemptCount <= 2) {
                  this.userFacade.initialize(previousSession.uid);
                }
                return true;
              }
            }

            // Ready to proceed
            return false;
          }),
          map(([user, token, _authInit, _userInit]) => {
            if (!token) {
              return this.navigateToSpecificPage(currentRoute, '/login');
            }

            // Tri-state registration routing fires for any hydrated user whose
            // status signals incomplete onboarding, independent of whether the
            // user has a stable id yet. Spec §4 invariant: pending users must
            // not reach /app/* under the OTP-first flow because OTP-verify now
            // creates a real session before the registration form is submitted.
            //
            // JOIN_REQUESTED users are treated like ACTIVE for routing — they
            // have a complete user doc, can hold N pending requests, and can
            // submit/withdraw more from /app/profile. OrgRequiredGuard handles
            // the org-less landing. This avoids the previous /account/info
            // dead-end where a mistyped PIB could lock a user out indefinitely.
            //
            // Guard skips redirect when the user already has org membership —
            // legacy/seeded users can carry an inconsistent PENDING_TO_JOIN
            // status while their orgId/orgIds are populated (mass-imports that
            // bypassed the manageOrgUser Cloud Function trigger that resets
            // status). Treating those as ACTIVE matches the backend's
            // effectiveRegistrationStatus and unblocks login without requiring
            // a data migration on every such drift.
            const hasOrgMembership = !!user?.orgId || (user?.orgIds?.length ?? 0) > 0;
            if (
              (user?.registrationStatus === NO_TEFTER_ACCOUNT || user?.registrationStatus === 'PENDING_TO_JOIN') &&
              !hasOrgMembership
            ) {
              if (currentRoute.startsWith('/account/register-user')) {
                this.attemptCount = 0;
                return true;
              }
              return this.navigateToSpecificPage(currentRoute, '/account/register-user');
            }

            // Handle previously authenticated users with loading delays
            if (!user || !user.id || user.id === NO_USER_ID) {
              const previousSession = this.getPreviousSessionData();

              // Previously authenticated user — require Firebase session and fresh cache
              if (
                previousSession &&
                !!this.auth.currentUser &&
                Date.now() - previousSession.timestamp < 60 * 60 * 1000
              ) {
                // If max attempts reached, proceed anyway
                if (this.attemptCount >= this.MAX_ATTEMPTS - 1) {
                  this.attemptCount = 0;
                  return true;
                }

                // Reload user data
                this.userFacade.initialize(previousSession.uid);
                return true;
              }
            }

            // Org-less users are allowed into /app — OrgRequiredGuard on each
            // child route handles the redirect to /app/profile. The stale
            // token-claims recovery below is skipped for genuinely org-less users.

            // Success! Save session and proceed
            if (user) {
              this.saveUserSessionState(user);
            }

            this.attemptCount = 0;

            if (environment.production && user) {
              Sentry.setUser({
                id: user.id,
                username: user.phoneNumber,
                name: user.fullName,
              });
            }

            return true;
          }),
          take(1)
        )
      );

      // Stale token-claims recovery. Only fires if the Firestore user record says
      // they belong to an org but the JWT lost the claim — refresh it. Genuinely
      // org-less users (no Firestore org + no token claim) fall through and reach
      // /app/profile via OrgRequiredGuard.
      if (authData === true) {
        const token = this.authQuery.getIdToken();
        const user = this.userQuery.getCurrentUser();
        const userHasOrg = !!user?.orgId || (user?.orgIds?.length ?? 0) > 0;
        if (token && userHasOrg && !token.claims.orgId && !token.claims.orgIds) {
          if (this.attemptCount <= 1) {
            await this.reloadAuthData();
            return this.router.parseUrl(currentRoute || '/app');
          }
          reportForcedSignOut({ trigger: 'org-claim-mismatch' });
          this.authService.logOut().catch((err) => {
            console.error('[AuthGuard] logOut failed during org-claim recovery:', err);
            Sentry.captureException(err, { tags: { component: 'AuthGuard', action: 'orgClaimLogout' } });
          });
          return this.navigateToSpecificPage(currentRoute, '/login');
        }
      }

      return authData;
    } catch (error) {
      console.error('🚨 AuthGuard: Error occurred', error);
      Sentry.captureException(error, { tags: { component: 'AuthGuard', action: 'canActivate' } });

      // Check cached session on error — require Firebase session
      const previousSession = this.getPreviousSessionData();
      if (previousSession && !!this.auth.currentUser && Date.now() - previousSession.timestamp < 60 * 60 * 1000) {
        this.attemptCount = 0;
        return true;
      }

      localStorage.removeItem('userSessionData');
      return this.navigateToSpecificPage(currentRoute, '/login');
    }
  }

  /**
   * Reload auth data
   */
  private async reloadAuthData(): Promise<void> {
    try {
      await this.authService.getFreshIdToken();
    } catch (error) {
      console.error('[AuthGuard] Error reloading auth data:', error);
      Sentry.captureException(error, { tags: { component: 'AuthGuard', action: 'reloadAuthData' } });
    }
  }

  private navigateToSpecificPage(currentRoute: string, urlPath: string): UrlTree {
    if (urlPath === '/login' && currentRoute !== '/login' && currentRoute !== '/app') {
      // Stamp the returnUrl's owner (the last-known user, if any) so a DIFFERENT user
      // logging in on a shared device isn't restored to this user's page. An anonymous
      // deep link (no known user) leaves it unstamped and is honored for whoever logs in.
      const ownerUid = toOwnerUid(this.userQuery.getCurrentUser()?.id);
      if (ownerUid) {
        sessionStorage.setItem(RETURN_URL_OWNER_KEY, ownerUid);
      }
      return this.router.parseUrl(`/login?returnUrl=${encodeURIComponent(currentRoute)}`);
    }
    return this.router.parseUrl(urlPath);
  }

  /**
   * Save user session state to localStorage
   */
  private saveUserSessionState(user: any) {
    if (user && user.id && user.id !== NO_USER_ID && user.registrationStatus !== NO_TEFTER_ACCOUNT) {
      localStorage.setItem(
        'userSessionData',
        JSON.stringify({
          uid: user.id,
          timestamp: Date.now(),
          registered: true,
          orgId: user.orgId,
        })
      );
    }
  }

  /**
   * Get previous session data from localStorage
   */
  private getPreviousSessionData(): SessionData | null {
    try {
      const sessionData = localStorage.getItem('userSessionData');
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error('Error parsing session data', error);
      localStorage.removeItem('userSessionData');
      return null;
    }
  }
}
